//@ts-nocheck
import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React from "react";
import { boolean, object } from "yup";
import VideoPlayer from "../../../components/data-display/VideoPlayer";

const PricingItem = ({ percentage, children, ...rest }) => {
  return (
    <Stack
      bg="neutral.100"
      py={[2, 3]}
      px={[2, 4]}
      rounded="fbRadius"
      textAlign="center"
      borderWidth="2xs"
      fontSize={["sm", "md", "lg"]}
      {...rest}
    >
      <Box>{children}</Box>
      <Text fontWeight="bold">{percentage}%</Text>
    </Stack>
  );
};

const placeholders = {
  watchVideo: {
    label: "I have watched the tutorial",
  },
  earnings: {
    label: "I understand how earnings are calculated",
  },
  payments: {
    label: "I understand how payments work",
  },
  taxPayment: {
    label: "I understand I'll pay 5% withholding tax",
  },
  penalty: {
    label: "I'll keep my prices updated",
  },
};

const validationSchema = object().shape({
  watchVideo: boolean().required().oneOf([true], "Please watch the video"),
  earnings: boolean().required().oneOf([true], "Please read and accept this"),
  payments: boolean().required().oneOf([true], "Please read and accept this"),
  taxPayment: boolean().required().oneOf([true], "Please read and accept this"),
  penalty: boolean().required().oneOf([true], "Please read and accept this"),
});

const commonProps = ({ name }, { touched, errors }) => ({
  ...placeholders[name],
  isInvalid: Boolean(touched[name] && errors[name]),
  errorMessage: errors[name],
});

export const HowPricingWorks: React.FC<{
  onBackClick: () => void;
  earningPercentage?: number;
  priceAgreement?: boolean;
  withholdingTax?: number;
  onSubmit: (values: any) => void;
}> = observer(
  ({
    onBackClick,
    earningPercentage,
    withholdingTax,
    priceAgreement,
    onSubmit,
  }) => {
    const initialValues = {
      watchVideo: priceAgreement,
      earnings: priceAgreement,
      payments: priceAgreement,
      taxPayment: priceAgreement,
      penalty: priceAgreement,
    };
    const tuteriaPercentage = 100 - earningPercentage;

    React.useEffect(() => window.scroll(0, 0), []);

    return (
      <Box className="how-pricing-works">
        <Button
          mb={6}
          onClick={onBackClick}
          leftIcon={<ArrowBackIcon />}
          variant="link"
          colorScheme="blue"
        >
          Back
        </Button>

        <Stack pb={6}>
          <Text fontSize="3xl" lineHeight="shorter" fontWeight="bold" as="h1">
            Learn how pricing works at Tuteria
          </Text>
          <Text mt={1} color="gray.700">
            Price is a major factor clients consider when hiring tutors. This
            quick guide helps you understand how pricing works and how to set
            competitive rates.
          </Text>
        </Stack>

        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onSubmit(values);
          }}
          validationSchema={validationSchema}
        >
          {({ handleSubmit, values }) => (
            <Form>
              <Stack spacing={8} pb={12}>
                <Stack spacing={3}>
                  <HStack>
                    <Text fontSize="lg" fontWeight="semibold" as="h2">
                      1. Watch this tutorial
                    </Text>
                    <Badge colorScheme="red">Important</Badge>
                  </HStack>
                  <AspectRatio
                    ratio={1}
                    width="100%"
                    rounded="lg"
                    overflow="hidden"
                    height={["2xs", "sm"]}
                  >
                    <VideoPlayer url="https://youtu.be/IdhKJYuKrf8" />
                  </AspectRatio>

                  {!priceAgreement && (
                    <Field
                      name="watchVideo"
                      render={({ field, form }) => (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <Checkbox
                            py={3}
                            size="lg"
                            {...field}
                            {...commonProps(field, form)}
                            borderColor="neutral.600"
                          >
                            <Text fontSize="md">
                              {commonProps(field, form).label}
                            </Text>
                          </Checkbox>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>

                <Divider borderColor="neutral.500" />

                <Stack>
                  <Text fontSize="lg" fontWeight="semibold" as="h2">
                    2. You earn most of what you charge
                  </Text>
                  <Flex py={3} align="center" justifyContent="space-between">
                    <PricingItem percentage={earningPercentage}>
                      What you earn
                    </PricingItem>
                    <Text mx={[2, 5]}>&#61;</Text>
                    <PricingItem percentage={100}>What you charge</PricingItem>
                    <Text mx={[2, 5]}>&minus;</Text>
                    <PricingItem percentage={tuteriaPercentage}>
                      Tuteria's fees
                    </PricingItem>
                  </Flex>
                  <Text>
                    New tutors earn 70% of lesson fees. By completing more
                    bookings, maintaining 4.5+ rating and offering great
                    service, you can earn 75% as a Premium tutor.
                  </Text>

                  <Text>
                    Your current earning level is {earningPercentage}%.
                  </Text>

                  <Text>
                    To ensure transparency, we don't add to your rates. What you
                    charge is what clients pay. Tuteria's fee is{" "}
                    <b>deducted from your rates</b> when you get bookings.
                  </Text>

                  {!priceAgreement && (
                    <Field
                      name="earnings"
                      render={({ field, form }) => (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <Checkbox
                            py={3}
                            size="lg"
                            {...field}
                            {...commonProps(field, form)}
                            borderColor="neutral.600"
                          >
                            <Text fontSize="md">
                              {commonProps(field, form).label}
                            </Text>
                          </Checkbox>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>

                <Divider borderColor="neutral.500" />

                <Stack>
                  <Text fontSize="lg" fontWeight="semibold" as="h2">
                    3. Your payment is always guaranteed
                  </Text>

                  <Text>
                    With Tuteria, you are never owed. Clients pay to us at the
                    start of lessons, while your earnings arrive in your bank
                    after all lessons have been satisfactorily delivered with no
                    pending issues to resolve.
                  </Text>

                  <Text>
                    Some bookings may qualify for transport support. If so,
                    it'll be paid along with your earnings at the end of
                    lessons.
                  </Text>

                  {!priceAgreement && (
                    <Field
                      name="payments"
                      render={({ field, form }) => (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <Checkbox
                            py={3}
                            size="lg"
                            {...field}
                            {...commonProps(field, form)}
                            borderColor="neutral.600"
                          >
                            <Text fontSize="md">
                              {commonProps(field, form).label}
                            </Text>
                          </Checkbox>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>

                <Divider borderColor="neutral.500" />

                <Stack>
                  <Text fontSize="lg" fontWeight="semibold" as="h2">
                    4. You will also pay taxes
                  </Text>

                  <Text>
                    We are legally required to deduct {withholdingTax}%
                    Withholding Tax from your earnings and remit to the State on
                    your behalf. This fee will appear on your earnings report,
                    and a tax receipt will be sent once it's received from the
                    State.
                  </Text>

                  {!priceAgreement && (
                    <Field
                      name="taxPayment"
                      render={({ field, form }) => (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <Checkbox
                            py={3}
                            size="lg"
                            {...field}
                            {...commonProps(field, form)}
                            borderColor="neutral.600"
                          >
                            <Text fontSize="md">
                              {commonProps(field, form).label}
                            </Text>
                          </Checkbox>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>

                <Divider borderColor="neutral.500" />

                <Stack>
                  <Text fontSize="lg" fontWeight="semibold" as="h2">
                    5. Keep your prices up-to-date
                  </Text>

                  <Text>
                    Prices you set will be shown to clients in search results.
                    You cannot decline booking requests that come in at your
                    price.
                  </Text>

                  <Text>
                    If you decline more than one request, your account will be
                    suspended for up to 4 months or permanently deactivated.
                  </Text>

                  <Text>
                    Ensure to keep your price updated. The most successful
                    tutors charge slightly low at first to draw clients, then
                    increased their rates as they got more reputation.
                  </Text>

                  {!priceAgreement && (
                    <Field
                      name="penalty"
                      render={({ field, form }) => (
                        <FormControl
                          isInvalid={commonProps(field, form).isInvalid}
                        >
                          <Checkbox
                            py={3}
                            size="lg"
                            {...field}
                            {...commonProps(field, form)}
                            borderColor="neutral.600"
                          >
                            <Text fontSize="md">
                              {commonProps(field, form).label}
                            </Text>
                          </Checkbox>

                          <FormErrorMessage>
                            {commonProps(field, form).errorMessage}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    />
                  )}
                </Stack>

                <Button mt={6} size="lg" colorScheme="blue" type="submit">
                  Continue
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    );
  },
);
